import { isMobilePlaySurface } from './mobileDetect';

export class Input {
  readonly keys = new Set<string>();
  mouseDX = 0;
  mouseDY = 0;
  pointerLocked = false;
  /** True while the right-side look pad is held. */
  touchLooking = false;

  private readonly canvas: HTMLCanvasElement;
  private jumpEdge = false;
  private interactEdge = false;
  private touchAxis = { x: 0, z: 0 };
  private touchRun = false;
  private zoomHeld = false;
  private readonly mobile = isMobilePlaySurface();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    window.addEventListener('keydown', (e) => {
      // Ignore OS key-repeat so held Space/E don't re-edge every frame.
      if (e.repeat) {
        if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) {
          e.preventDefault();
        }
        return;
      }
      this.keys.add(e.code);
      if (e.code === 'Space') this.jumpEdge = true;
      if (e.code === 'KeyE') this.interactEdge = true;
      if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) {
        e.preventDefault();
      }
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));

    // Sticky-key / stuck-axis fix: clear when focus leaves the page or lock drops.
    window.addEventListener('blur', () => this.clearKeys());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.clearKeys();
    });

    document.addEventListener('pointerlockchange', () => {
      const locked = document.pointerLockElement === this.canvas;
      this.pointerLocked = locked;
      if (!locked) this.clearKeys();
    });

    document.addEventListener('pointerlockerror', () => {
      this.pointerLocked = false;
    });

    document.addEventListener('mousemove', (e) => {
      if (!this.pointerLocked) return;
      this.mouseDX += e.movementX;
      this.mouseDY += e.movementY;
    });

    // ICO R2 zoom — hold right mouse (or R) to draw the observer closer.
    this.canvas.addEventListener('mousedown', (e) => {
      if (e.button === 2) this.zoomHeld = true;
    });
    this.canvas.addEventListener('mouseup', (e) => {
      if (e.button === 2) this.zoomHeld = false;
    });
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  wantsPointerLock(): boolean {
    return !this.mobile;
  }

  /** Camera can look when pointer-locked or using the touch look pad. */
  canLook(): boolean {
    return this.pointerLocked || this.touchLooking || this.mobile;
  }

  setTouchAxis(x: number, y: number): void {
    // Stick: +x right, +y down → game: +x strafe, +z back
    this.touchAxis = { x, z: y };
  }

  setTouchRun(on: boolean): void {
    this.touchRun = on;
  }

  setTouchCrouch(on: boolean): void {
    if (on) this.keys.add('KeyC');
    else this.keys.delete('KeyC');
  }

  addTouchLook(dx: number, dy: number): void {
    this.mouseDX += dx;
    this.mouseDY += dy;
  }

  pulseTouchJump(): void {
    this.jumpEdge = true;
    this.keys.add('Space');
    window.setTimeout(() => this.keys.delete('Space'), 120);
  }

  pulseTouchInteract(): void {
    this.interactEdge = true;
    this.keys.add('KeyE');
    window.setTimeout(() => this.keys.delete('KeyE'), 120);
  }

  clearKeys(): void {
    this.keys.clear();
    this.jumpEdge = false;
    this.interactEdge = false;
    this.touchAxis = { x: 0, z: 0 };
    this.touchRun = false;
    this.zoomHeld = false;
  }

  /** ICO R2 — hold to zoom toward the pair. */
  zooming(): boolean {
    return this.zoomHeld || this.keys.has('KeyR');
  }

  requestLock(): void {
    if (!this.wantsPointerLock()) return;
    if (document.pointerLockElement === this.canvas) {
      this.pointerLocked = true;
      return;
    }
    // Must run from a user gesture; ignore rejection (Esc / browser policy).
    const req = this.canvas.requestPointerLock();
    if (req && typeof (req as Promise<void>).catch === 'function') {
      (req as Promise<void>).catch(() => {
        this.pointerLocked = false;
      });
    }
  }

  consumeMouse(): { dx: number; dy: number } {
    const out = { dx: this.mouseDX, dy: this.mouseDY };
    this.mouseDX = 0;
    this.mouseDY = 0;
    return out;
  }

  /** Camera-relative move axes: x = strafe, z = forward(-1)/back(+1). */
  axis(): { x: number; z: number } {
    let x = this.touchAxis.x;
    let z = this.touchAxis.z;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) x -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) x += 1;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) z -= 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) z += 1;
    const len = Math.hypot(x, z);
    if (len > 1e-5) {
      x /= len;
      z /= len;
    }
    return { x, z };
  }

  running(): boolean {
    return this.touchRun || this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
  }

  /** Held Space (for mid-climb leap / air-grab hold). */
  jumpPressed(): boolean {
    return this.keys.has('Space');
  }

  /** Rising edge of Space (consumes). */
  jumpJustPressed(): boolean {
    if (!this.jumpEdge) return false;
    this.jumpEdge = false;
    return true;
  }

  interactPressed(): boolean {
    return this.keys.has('KeyE');
  }

  /** Rising edge of E (consumes). */
  interactJustPressed(): boolean {
    if (!this.interactEdge) return false;
    this.interactEdge = false;
    return true;
  }
}
